/**
 * DoorDash API service
 * Handles all DoorDash Drive integration endpoints
 *
 * @see https://developer.doordash.com/docs/drive/overview
 */

import type { AxiosInstance, AxiosResponse } from 'axios';
import axios from 'axios';
import type {
    ApiDoorDashQuoteParams,
    ApiDoorDashQuoteGenerationResponse,
    ApiDoorDashQuoteAcceptanceResponse,
    ApiMultiCarrierQuoteResponse,
    ApiDoorDashConfigRequest,
    ApiDoorDashConfigResponse,
    ApiDoorDashGetConfigResponse,
    ApiDoorDashSpendResponse,
    ApiStripeSetupIntentResponse,
    ApiStripePaymentMethodResponse
} from './types/ApiDoorDash';
import type { AxiosApiResponse } from '~/api/types/IApiResponse';
import {
    getAcceptMultiCarrierQuoteParamErrors,
    getAcceptQuoteParamErrors,
    getGenerateMultiCarrierQuoteParamErrors,
    getGenerateQuoteParamErrors
} from './validate';
import constants from '~/utils/constants';
import { getIntegrationsBaseUrl } from './DoorDashApi.config';

/**
 * Create an isolated axios instance for DoorDash API calls
 * This prevents interference with the main app's axios configuration
 */
const createDoorDashAxiosInstance = (): AxiosInstance => {
    const baseURL = getIntegrationsBaseUrl();

    /**
     * Build headers once using axios.defaults, then attach to the instance.
     * This replaces the previous inline object-spread approach.
     */
    const updatedHeaders: Record<string, string> = {
        ...(constants.requestHeaders as Record<string, string>)
    };

    const commonHeaders =
        (axios.defaults?.headers?.common as Record<string, unknown>) ?? {};
    const rootDefaults = axios.defaults?.headers as Record<string, unknown>;

    if (commonHeaders.Authorization) {
        updatedHeaders.Authorization = commonHeaders.Authorization as string;
    }
    if (commonHeaders['X-OAuth-IDToken']) {
        updatedHeaders['X-OAuth-IDToken'] = commonHeaders[
            'X-OAuth-IDToken'
        ] as string;
    }

    const wiseClientIdKey = (
        constants.requestHeaders as Record<string, unknown>
    ).WISE_CLIENT_ID as string | undefined;
    const wiseClientIdsKey = (
        constants.requestHeaders as Record<string, unknown>
    ).WISE_CLIENT_IDS as string | undefined;

    if (wiseClientIdKey) {
        const val =
            commonHeaders[wiseClientIdKey] ??
            (rootDefaults ? rootDefaults[wiseClientIdKey] : undefined);
        if (val) updatedHeaders[wiseClientIdKey] = val as string;
    }

    if (wiseClientIdsKey) {
        const val =
            commonHeaders[wiseClientIdsKey] ??
            (rootDefaults ? rootDefaults[wiseClientIdsKey] : undefined);
        if (val) updatedHeaders[wiseClientIdsKey] = val as string;
    }

    const instance = axios.create({
        baseURL,
        headers: updatedHeaders
    });

    /**
     * Add request interceptor to ensure headers are always up-to-date
     * Guard against undefined instance in test environments
     */
    if (instance && instance.interceptors) {
        instance.interceptors.request.use((config) => {
            const common =
                (axios.defaults?.headers?.common as Record<string, unknown>) ??
                {};
            const root = axios.defaults?.headers as Record<string, unknown>;

            config.headers = config.headers ?? {};

            if (common.Authorization) {
                (config.headers as Record<string, unknown>).Authorization =
                    common.Authorization as string;
            }
            if (common['X-OAuth-IDToken']) {
                (config.headers as Record<string, unknown>)['X-OAuth-IDToken'] =
                    common['X-OAuth-IDToken'] as string;
            }

            if (wiseClientIdKey) {
                const v =
                    common[wiseClientIdKey] ??
                    (root ? root[wiseClientIdKey] : undefined);
                if (v)
                    (config.headers as Record<string, unknown>)[
                        wiseClientIdKey
                    ] = v as string;
            }
            if (wiseClientIdsKey) {
                const v =
                    common[wiseClientIdsKey] ??
                    (root ? root[wiseClientIdsKey] : undefined);
                if (v)
                    (config.headers as Record<string, unknown>)[
                        wiseClientIdsKey
                    ] = v as string;
            }

            return config;
        });
    }

    return instance;
};

const doorDashAxios = createDoorDashAxiosInstance();

/**
 * Implementation of API methods for DoorDash integration
 * Following the standard class-based API pattern
 *
 * @category API
 * @module api/DoorDashApi
 */
export default class DoorDashApi {
    /**
     * the API endpoint base path
     */
    private static readonly doorDashPath = '/doordash';

    /**
     * Key for react-query
     */
    static readonly REACT_QUERY_KEY_DOORDASH = 'doordash';

    /**
     * React-query sub-key for the daily spend query
     */
    static readonly REACT_QUERY_KEY_DOORDASH_SPEND = 'spend';

    /**
     * Generate a DoorDash quote for a task
     * @method generateQuote
     * @static
     * @param taskId The task ID
     * @param params Quote generation parameters
     * @return {Promise<AxiosApiResponse<ApiDoorDashQuoteGenerationResponse>>}
     */
    static generateQuote(
        taskId: string,
        params: ApiDoorDashQuoteParams
    ): Promise<
        AxiosApiResponse<
            ApiDoorDashQuoteGenerationResponse,
            Record<string, never>
        >
    > {
        const paramError = getGenerateQuoteParamErrors({ taskId, params });
        if (paramError) {
            return Promise.reject(new Error(paramError));
        }

        return doorDashAxios.put(`${this.doorDashPath}/quote`, params, {
            params: new URLSearchParams({ taskId }),
            headers: constants.requestHeaders
        });
    }

    /**
     * Accept a DoorDash quote for a task
     * @method acceptQuote
     * @static
     * @param taskId The task ID
     * @return {Promise<AxiosApiResponse<ApiDoorDashQuoteAcceptanceResponse>>}
     */
    static acceptQuote(
        taskId: string,
        paymentIntentId?: string
    ): Promise<
        AxiosApiResponse<
            ApiDoorDashQuoteAcceptanceResponse,
            Record<string, never>
        >
    > {
        const paramError = getAcceptQuoteParamErrors({ taskId });
        if (paramError) {
            return Promise.reject(new Error(paramError));
        }

        const params = new URLSearchParams({ taskId });
        if (paymentIntentId) {
            params.set('paymentIntentId', paymentIntentId);
        }

        return doorDashAxios.put(`${this.doorDashPath}/quote/accept`, null, {
            params,
            headers: constants.requestHeaders
        });
    }

    /**
     * Save DoorDash configuration (billing, markup, defaults)
     * @method saveConfig
     * @static
     * @param config The configuration to save
     * @return {Promise<AxiosApiResponse<ApiDoorDashConfigResponse>>}
     */
    static saveConfig(
        config: ApiDoorDashConfigRequest
    ): Promise<
        AxiosApiResponse<ApiDoorDashConfigResponse, Record<string, never>>
    > {
        return doorDashAxios.put(`${this.doorDashPath}/config`, config, {
            headers: constants.requestHeaders
        });
    }

    /**
     * Get DoorDash configuration from SSM
     * @method getConfig
     * @static
     * @return {Promise<AxiosApiResponse<ApiDoorDashGetConfigResponse>>}
     */
    static getConfig(): Promise<AxiosResponse<ApiDoorDashGetConfigResponse>> {
        return doorDashAxios.get(`${this.doorDashPath}/config`, {
            headers: constants.requestHeaders
        });
    }

    /**
     * Get daily DoorDash spend totals for the usage chart
     * @method getSpend
     * @static
     * @param days Window size in days (server default 30, max 90)
     * @return {Promise<AxiosResponse<ApiDoorDashSpendResponse>>}
     */
    static getSpend(
        days?: number
    ): Promise<AxiosResponse<ApiDoorDashSpendResponse>> {
        return doorDashAxios.get(`${this.doorDashPath}/spend`, {
            params: days === undefined ? undefined : { days },
            headers: constants.requestHeaders
        });
    }

    /**
     * Create a Stripe customer and return a SetupIntent
     * @method createStripeCustomer
     * @static
     * @return {Promise<AxiosApiResponse<ApiStripeSetupIntentResponse>>}
     */
    static createStripeCustomer(): Promise<
        AxiosResponse<ApiStripeSetupIntentResponse>
    > {
        return doorDashAxios.put('/stripe/customer', null, {
            headers: constants.requestHeaders
        });
    }

    /**
     * Create a Stripe SetupIntent for an existing customer
     * @method createSetupIntent
     * @static
     * @return {Promise<AxiosApiResponse<ApiStripeSetupIntentResponse>>}
     */
    static createSetupIntent(): Promise<
        AxiosResponse<ApiStripeSetupIntentResponse>
    > {
        return doorDashAxios.put('/stripe/payment_method', null, {
            headers: constants.requestHeaders
        });
    }

    /**
     * Get the saved Stripe payment method details
     * @method getPaymentMethod
     * @static
     * @return {Promise<AxiosApiResponse<ApiStripePaymentMethodResponse>>}
     */
    static getPaymentMethod(): Promise<
        AxiosResponse<ApiStripePaymentMethodResponse>
    > {
        return doorDashAxios.get('/stripe/payment_method', {
            headers: constants.requestHeaders
        });
    }

    /** Generate quotes from all enabled carriers */
    static generateMultiCarrierQuote(
        taskId: string,
        params: ApiDoorDashQuoteParams
    ): Promise<
        AxiosApiResponse<ApiMultiCarrierQuoteResponse, Record<string, never>>
    > {
        const paramError = getGenerateMultiCarrierQuoteParamErrors({
            taskId,
            params
        });
        if (paramError) {
            return Promise.reject(new Error(paramError));
        }

        return doorDashAxios.put(
            `${this.doorDashPath}/carriers/quote`,
            params,
            {
                params: new URLSearchParams({ taskId }),
                headers: constants.requestHeaders
            }
        );
    }

    /** Accept a multi-carrier quote */
    static acceptMultiCarrierQuote(
        taskId: string,
        quoteId: string,
        carrierId: string
    ): Promise<
        AxiosApiResponse<
            ApiDoorDashQuoteAcceptanceResponse,
            Record<string, never>
        >
    > {
        const paramError = getAcceptMultiCarrierQuoteParamErrors({
            taskId,
            quoteId,
            carrierId
        });
        if (paramError) {
            return Promise.reject(new Error(paramError));
        }

        return doorDashAxios.put(
            `${this.doorDashPath}/carriers/quote/accept`,
            null,
            {
                params: new URLSearchParams({ taskId, quoteId, carrierId }),
                headers: constants.requestHeaders
            }
        );
    }

    /** Get carrier marketplace configuration */
    static getCarrierConfig(): Promise<AxiosResponse<Record<string, unknown>>> {
        return doorDashAxios.get(`${this.doorDashPath}/carriers/config`, {
            headers: constants.requestHeaders
        });
    }

    /** Save carrier marketplace configuration */
    static saveCarrierConfig(
        payload: Record<string, unknown>
    ): Promise<AxiosResponse<Record<string, unknown>>> {
        return doorDashAxios.put(
            `${this.doorDashPath}/carriers/config`,
            payload,
            { headers: constants.requestHeaders }
        );
    }
}
